import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';

/**
 * Reusable payment wall. Shown wherever a feature is gated behind a higher
 * plan (the backend throws a 403 detected by isPlanGateError). Replaces the
 * old ad-hoc "raw error message" rendering with a clear Upgrade button that
 * routes to the dedicated in-app Subscription page.
 */
export function UpgradeWall({
  wsId,
  requiredTier,
  featureName,
  description,
}: {
  wsId: string;
  requiredTier: 'growth' | 'enterprise';
  featureName: string;
  description?: string;
}) {
  const { t } = useTranslation();
  const tierLabel = requiredTier === 'enterprise' ? 'Enterprise' : 'Growth';
  return (
    <div className="rounded-2xl border border-accent-200 bg-accent-50 px-5 py-4">
      <div className="flex items-start gap-3">
        <Lock className="h-5 w-5 text-accent-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-ink-900">
            {t('upgrade.title', { defaultValue: `${featureName} is a ${tierLabel} feature.`, feature: featureName, tier: tierLabel })}
          </p>
          <p className="mt-1 text-sm text-ink-600">
            {description ?? t('upgrade.body', { defaultValue: `Upgrade to ${tierLabel} to unlock it — pick a plan and you're set in a minute.`, tier: tierLabel })}
          </p>
          <Link
            to={`/app/workspace/${wsId}/subscription`}
            className="mt-3 inline-flex items-center rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-white hover:bg-accent-600"
          >
            {t('upgrade.button', { defaultValue: 'Upgrade' })}
          </Link>
        </div>
      </div>
    </div>
  );
}
